// Fallback consent banner — shown only when no CMP is detected and the
// deployment's consent fallback is "banner" (ANALYTICS-SYSTEM-DESIGN.md §3.3).
//
// Plain DOM, no React: the banner must work from the embed-loader before any
// React tree exists, and must never fight the host page's framework. Rendered
// into its own Shadow DOM host so host CSS can't break it and ours can't leak.
//
// The choice itself is persisted in localStorage — storing the refusal is
// strictly necessary to honor it, so this is permitted without consent.
// ESC is intentionally ignored: the visitor must make an explicit choice
// (dismissing without choosing would force us to re-prompt every page).

export interface StoredConsentChoice {
  /** "full" = statistics granted (tier 2), "cookieless" = declined. */
  tier: "full" | "cookieless";
  version?: string;
  /** Unix ms of the choice. */
  at: number;
}

export interface BannerStrings {
  message: string;
  accept: string;
  decline: string;
  privacy: string;
  essential: string;
  statistics: string;
  save: string;
  acceptAll: string;
}

export interface BannerOptions {
  /** Mount point — the embed container (script embed) or null/undefined for
   *  document.body (host SPA case). */
  container?: HTMLElement | null;
  style?: "minimal" | "categories";
  /** Short language code ("en", "da"/"dk", ...). Defaults to navigator.language. */
  language?: string;
  privacyUrl?: string;
  /** Bootstrap-provided string overrides (keys of BannerStrings). */
  strings?: Record<string, string>;
  /** Consent copy version recorded with the choice. */
  version?: string;
  /** API origin for POST /api/consent/record. */
  apiBase?: string;
  sessionId?: string | null;
  getVisitorId?: () => string | null;
  onChoice: (tier: "full" | "cookieless") => void;
}

export interface BannerHandle {
  destroy(): void;
}

export const CONSENT_CHOICE_KEY = "dconfig-consent";

const EN: BannerStrings = {
  message:
    "We use anonymous statistics to improve this configurator. Allow extended statistics (remembers your visit)?",
  accept: "Accept",
  decline: "Decline",
  privacy: "Privacy policy",
  essential: "Essential (always on)",
  statistics: "Statistics",
  save: "Save choices",
  acceptAll: "Accept all",
};

const DA: BannerStrings = {
  message:
    "Vi bruger anonym statistik til at forbedre denne konfigurator. Tillad udvidet statistik (husker dit besøg)?",
  accept: "Accepter",
  decline: "Afvis",
  privacy: "Privatlivspolitik",
  essential: "Nødvendige (altid slået til)",
  statistics: "Statistik",
  save: "Gem valg",
  acceptAll: "Accepter alle",
};

function isDanish(language?: string): boolean {
  const lang = (language ?? safeNavigatorLanguage() ?? "").toLowerCase();
  return lang.startsWith("da") || lang === "dk" || lang.startsWith("dk-");
}

function safeNavigatorLanguage(): string | null {
  try {
    return typeof navigator !== "undefined" ? navigator.language : null;
  } catch {
    return null;
  }
}

function resolveStrings(opts: BannerOptions): BannerStrings {
  const base = isDanish(opts.language) ? DA : EN;
  if (!opts.strings) return base;
  const merged: BannerStrings = { ...base };
  for (const key of Object.keys(base) as (keyof BannerStrings)[]) {
    const v = opts.strings[key];
    if (typeof v === "string" && v.trim() !== "") merged[key] = v;
  }
  return merged;
}

/** Reads the stored explicit banner choice, or null when the visitor has not
 *  chosen yet (or storage is unavailable). */
export function readStoredBannerChoice(): StoredConsentChoice | null {
  try {
    const raw = window.localStorage.getItem(CONSENT_CHOICE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsentChoice;
    if (parsed && (parsed.tier === "full" || parsed.tier === "cookieless")) return parsed;
    return null;
  } catch {
    return null;
  }
}

function storeChoice(tier: "full" | "cookieless", version?: string): void {
  try {
    const choice: StoredConsentChoice = { tier, version, at: Date.now() };
    window.localStorage.setItem(CONSENT_CHOICE_KEY, JSON.stringify(choice));
  } catch {
    /* storage unavailable — the choice still applies for this page. */
  }
}

/** Fire-and-forget mirror of the choice into consent_records. Only called for
 *  OUR banner's choices — CMP-derived states are recorded by the CMP itself. */
function recordChoice(opts: BannerOptions, tier: "full" | "cookieless"): void {
  if (opts.apiBase === undefined) return;
  try {
    const body = JSON.stringify({
      type: "cookie_consent",
      tier,
      source: "own-banner",
      sessionId: opts.sessionId ?? null,
      visitorId: opts.getVisitorId ? opts.getVisitorId() : null,
      version: opts.version ?? null,
      context: { language: opts.language ?? null, style: opts.style ?? "minimal" },
    });
    fetch(`${opts.apiBase}/api/consent/record`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    /* never let consent recording break the page */
  }
}

const BANNER_CSS = `
:host { all: initial; }
* { box-sizing: border-box; }
.dc-banner {
  position: fixed;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  z-index: 2147483000;
  width: calc(100vw - 32px);
  max-width: 560px;
  background: #1f2428;
  color: #e6e8ea;
  border: 1px solid #3a4046;
  border-radius: 10px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.35);
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  font-size: 13px;
  line-height: 1.45;
  padding: 14px 16px;
}
.dc-banner__msg { margin: 0 0 10px; }
.dc-banner__row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.dc-banner__spacer { flex: 1 1 auto; }
.dc-banner__link {
  color: #9ab8d8;
  text-decoration: underline;
  font-size: 12px;
}
.dc-banner__btn {
  appearance: none;
  border: 1px solid #4a5158;
  border-radius: 7px;
  background: #2a3036;
  color: #e6e8ea;
  font: inherit;
  padding: 7px 14px;
  cursor: pointer;
}
.dc-banner__btn:hover { background: #343b42; }
.dc-banner__btn--primary {
  background: #e6e8ea;
  border-color: #e6e8ea;
  color: #1f2428;
  font-weight: 600;
}
.dc-banner__btn--primary:hover { background: #ffffff; }
.dc-banner__btn:focus-visible,
.dc-banner__link:focus-visible,
.dc-banner__toggle input:focus-visible {
  outline: 2px solid #9ab8d8;
  outline-offset: 2px;
}
.dc-banner__cats { display: flex; flex-direction: column; gap: 6px; margin: 0 0 12px; }
.dc-banner__toggle { display: flex; align-items: center; gap: 8px; }
.dc-banner__toggle input { accent-color: #9ab8d8; width: 15px; height: 15px; }
.dc-banner__toggle--locked { opacity: 0.65; }
`;

/**
 * Shows the fallback banner. Returns a handle whose destroy() removes it.
 * The banner removes itself after a choice is made.
 */
export function showConsentBanner(opts: BannerOptions): BannerHandle {
  const strings = resolveStrings(opts);
  const style = opts.style ?? "minimal";

  const parent = opts.container ?? document.body;
  const host = document.createElement("div");
  host.setAttribute("data-dconfig-consent-banner", "");
  const shadow = host.attachShadow({ mode: "open" });

  const styleEl = document.createElement("style");
  styleEl.textContent = BANNER_CSS;
  shadow.appendChild(styleEl);

  const box = document.createElement("div");
  box.className = "dc-banner";
  box.setAttribute("role", "dialog");
  box.setAttribute("aria-live", "polite");
  box.setAttribute("aria-label", strings.statistics);
  shadow.appendChild(box);

  const msg = document.createElement("p");
  msg.className = "dc-banner__msg";
  msg.textContent = strings.message;
  box.appendChild(msg);

  let statsChecked = true;

  if (style === "categories") {
    const cats = document.createElement("div");
    cats.className = "dc-banner__cats";

    const essential = document.createElement("label");
    essential.className = "dc-banner__toggle dc-banner__toggle--locked";
    const essentialInput = document.createElement("input");
    essentialInput.type = "checkbox";
    essentialInput.checked = true;
    essentialInput.disabled = true;
    essential.appendChild(essentialInput);
    essential.appendChild(document.createTextNode(strings.essential));
    cats.appendChild(essential);

    const stats = document.createElement("label");
    stats.className = "dc-banner__toggle";
    const statsInput = document.createElement("input");
    statsInput.type = "checkbox";
    statsInput.checked = true;
    statsInput.addEventListener("change", () => {
      statsChecked = statsInput.checked;
    });
    stats.appendChild(statsInput);
    stats.appendChild(document.createTextNode(strings.statistics));
    cats.appendChild(stats);

    box.appendChild(cats);
  }

  const row = document.createElement("div");
  row.className = "dc-banner__row";
  box.appendChild(row);

  if (opts.privacyUrl) {
    const link = document.createElement("a");
    link.className = "dc-banner__link";
    link.href = opts.privacyUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.textContent = strings.privacy;
    row.appendChild(link);
  }

  const spacer = document.createElement("div");
  spacer.className = "dc-banner__spacer";
  row.appendChild(spacer);

  let destroyed = false;
  const destroy = () => {
    if (destroyed) return;
    destroyed = true;
    try {
      host.remove();
    } catch {
      /* already detached */
    }
  };

  const choose = (tier: "full" | "cookieless") => {
    storeChoice(tier, opts.version);
    recordChoice(opts, tier);
    destroy();
    opts.onChoice(tier);
  };

  if (style === "categories") {
    const save = document.createElement("button");
    save.type = "button";
    save.className = "dc-banner__btn";
    save.textContent = strings.save;
    save.addEventListener("click", () => choose(statsChecked ? "full" : "cookieless"));
    row.appendChild(save);

    const acceptAll = document.createElement("button");
    acceptAll.type = "button";
    acceptAll.className = "dc-banner__btn dc-banner__btn--primary";
    acceptAll.textContent = strings.acceptAll;
    acceptAll.addEventListener("click", () => choose("full"));
    row.appendChild(acceptAll);
  } else {
    const decline = document.createElement("button");
    decline.type = "button";
    decline.className = "dc-banner__btn";
    decline.textContent = strings.decline;
    decline.addEventListener("click", () => choose("cookieless"));
    row.appendChild(decline);

    const accept = document.createElement("button");
    accept.type = "button";
    accept.className = "dc-banner__btn dc-banner__btn--primary";
    accept.textContent = strings.accept;
    accept.addEventListener("click", () => choose("full"));
    row.appendChild(accept);
  }

  // Deliberately no Escape handler and no close button — the banner only goes
  // away through an explicit choice (or programmatic destroy()).

  parent.appendChild(host);
  return { destroy };
}
